import { useState, useEffect } from 'react';
import { db } from '../../config/FirebaseConfig';
import { collection, addDoc, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore';
import { FiPlus, FiTrash2, FiEdit2, FiX } from 'react-icons/fi';
import {
  FiFacebook,
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiYoutube,
  FiGithub,
  FiMail,
  FiPhone,
} from 'react-icons/fi';
import { FaWhatsapp, FaTelegramPlane } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';

const AdminSocialMedia = () => {
  const [socialMedia, setSocialMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    platform: 'facebook',
    label: '',
    url: '',
    icon: 'facebook',
  });

  const platforms = [
    { id: 'facebook', name: 'Facebook', icon: FiFacebook },
    { id: 'instagram', name: 'Instagram', icon: FiInstagram },
    { id: 'twitter', name: 'Twitter', icon: FiTwitter },
    { id: 'linkedin', name: 'LinkedIn', icon: FiLinkedin },
    { id: 'youtube', name: 'YouTube', icon: FiYoutube },
    { id: 'github', name: 'GitHub', icon: FiGithub },
    { id: 'whatsapp', name: 'WhatsApp', icon: FaWhatsapp },
    { id: 'telegram', name: 'Telegram', icon: FaTelegramPlane },
    { id: 'tiktok', name: 'TikTok', icon: SiTiktok },
    { id: 'email', name: 'Email', icon: FiMail },
    { id: 'phone', name: 'Teléfono', icon: FiPhone },
  ];

  useEffect(() => {
    fetchSocialMedia();
  }, []);

  const fetchSocialMedia = async () => {
    try {
      setLoading(true);
      const socialCollection = collection(db, 'socialMedia');
      const snapshot = await getDocs(socialCollection);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSocialMedia(data);
    } catch (error) {
      console.error('Error al obtener redes sociales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.label.trim() || !formData.url.trim()) {
      alert('Por favor completa todos los campos');
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        await updateDoc(doc(db, 'socialMedia', editingId), {
          ...formData,
          updatedAt: new Date(),
        });
        alert('Red social actualizada correctamente');
      } else {
        await addDoc(collection(db, 'socialMedia'), {
          ...formData,
          createdAt: new Date(),
        });
        alert('Red social agregada correctamente');
      }
      setFormData({ platform: 'facebook', label: '', url: '', icon: 'facebook' });
      setEditingId(null);
      setShowForm(false);
      fetchSocialMedia();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar la red social');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta red social?')) return;

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'socialMedia', id));
      alert('Red social eliminada correctamente');
      fetchSocialMedia();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar la red social');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (social) => {
    setFormData({
      platform: social.platform,
      label: social.label,
      url: social.url,
      icon: social.icon || social.platform,
    });
    setEditingId(social.id);
    setShowForm(true);
  };

  const getIconComponent = (iconName) => {
    const platform = platforms.find((p) => p.id === iconName);
    return platform?.icon;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Gestión de Redes Sociales
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
            Administra tus enlaces de redes sociales que aparecerán en el header y footer
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) setEditingId(null);
            setFormData({ platform: 'facebook', label: '', url: '', icon: 'facebook' });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-semibold transition-colors"
        >
          <FiPlus size={20} />
          Agregar Red Social
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {editingId ? 'Editar Red Social' : 'Nueva Red Social'}
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            >
              <FiX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Plataforma
              </label>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {platforms.map((platform) => {
                  const Icon = platform.icon;
                  const isSelected = formData.icon === platform.id;
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          platform: platform.id,
                          icon: platform.id,
                        })
                      }
                      className={`
                        flex items-center justify-center w-12 h-12 rounded-lg transition-all
                        ${
                          isSelected
                            ? 'bg-pink-500 text-white scale-110'
                            : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }
                      `}
                      title={platform.name}
                    >
                      <Icon size={20} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Etiqueta/Nombre
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="ej: Facebook Oficial"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                URL / Enlace
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://facebook.com/tupagina"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ platform: 'facebook', label: '', url: '', icon: 'facebook' });
                }}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Social Media List */}
      {loading && !showForm ? (
        <div className="text-center py-8 text-slate-600 dark:text-slate-400">
          Cargando redes sociales...
        </div>
      ) : socialMedia.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            No hay redes sociales configuradas aún
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-semibold transition-colors"
          >
            <FiPlus size={20} />
            Agregar primera red social
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {socialMedia.map((social) => {
            const Icon = getIconComponent(social.icon);
            return (
              <div
                key={social.id}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex items-center justify-between hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4">
                  {Icon && (
                    <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                      <Icon size={24} className="text-pink-500" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {social.label}
                    </h3>
                    <a
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-xs"
                    >
                      {social.url}
                    </a>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(social)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-950 text-blue-600 dark:text-blue-400 rounded transition-colors"
                  >
                    <FiEdit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(social.id)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-950 text-red-600 dark:text-red-400 rounded transition-colors"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSocialMedia;
